import React, { FC, ReactElement } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { forTablet } from '@westwing/ui-kit/breakPoints';
import { FilterType, pathFilterNames } from '../../../redux/products/types';
import { SetListingPageStateFunc } from '../hooks/useListingPageUrlState';
import StyledSlider from '../../Generic/StyledSlider';
import { CategoriesState } from '../../../redux/category/types';
import { getSingleDisplayValue } from '../../../filterDisplayValueService';
import StateInterface from '../../../redux/types';
import AppliedFilterItem, { RemoveFilterFunc } from 'Client/components/Generic/AppliedFilters/AppliedFilterItem';
import { AppliedFilter, Filter } from 'Client/components/Generic/FilterAndSortBox/types';

const AppliedFilterStyledSlider = styled(StyledSlider)`
    padding-left: 0;

    ${forTablet`
        padding: 0;
    `}
`;

const AppliedFilterStyledSliderWrapper = styled.div<{ hide: boolean }>`
    display: ${({ hide }) => (hide ? 'none' : 'block')};
    height: 34px;
    overflow-y: hidden;
    margin-left: auto;
    margin-right: auto;
    ${forTablet`
        height: auto;
    `}
`;

interface OwnProps {
    updateFilters: SetListingPageStateFunc;
    appliedFiltersProps: AppliedFilter<FilterType>[];
    currency: string;
}

interface FromState {
    filters: Filter[];
    categories: CategoriesState;
}

export const removeValueFromFilters = (
    filterName: string,
    filterValue: string,
    appliedFilters: AppliedFilter<FilterType>[]
) => {
    const filterCopy = [...appliedFilters];
    const filterIndex = appliedFilters.findIndex(filter => filter.name === filterName);

    if (filterIndex < 0) {
        return filterCopy;
    }

    filterCopy[filterIndex].values = filterCopy[filterIndex].values.filter(value => value !== filterValue);
    return filterCopy.filter(filter => filter.values.length > 0);
};

const removeFilterFuncFactory = (
    filterName: string,
    filterValue: string,
    appliedFilters: AppliedFilter<FilterType>[],
    setListingPageStateFunc: SetListingPageStateFunc
    // eslint-disable-next-line max-params
): RemoveFilterFunc => () => {
    setListingPageStateFunc({
        appliedFilters: removeValueFromFilters(filterName, filterValue, appliedFilters),
    });
};

type AppliedFiltersSliderProps = OwnProps & FromState;

const AppliedFiltersSlider: FC<AppliedFiltersSliderProps> = ({
    appliedFiltersProps,
    updateFilters,
    currency,
    categories,
    filters,
}): ReactElement => {
    let rows = appliedFiltersProps
        .map(filter =>
            filter.values.map(value => ({
                filter,
                value: getSingleDisplayValue(value, filter.name, { categories, currency, filters }),
                rawValue: value,
            }))
        )
        .reduce((allValues, values) => [...allValues, ...values], []);

    if (appliedFiltersProps.length === 1 && appliedFiltersProps[0].name === pathFilterNames.categories) {
        rows = [];
    }

    return (
        <div>
            <AppliedFilterStyledSliderWrapper hide={rows.length === 0}>
                <AppliedFilterStyledSlider>
                    {rows.map(({ filter, value, rawValue }) => (
                        <AppliedFilterItem
                            name={value}
                            key={value}
                            removeFilterFunc={removeFilterFuncFactory(
                                filter.name,
                                rawValue,
                                appliedFiltersProps,
                                updateFilters
                            )}
                        />
                    ))}
                </AppliedFilterStyledSlider>
            </AppliedFilterStyledSliderWrapper>
        </div>
    );
};

const mapStateToProps = (state: StateInterface): FromState => ({
    filters: state.products.filters,
    categories: state.categories,
});

export default connect(mapStateToProps)(React.memo(AppliedFiltersSlider));
